import type { ReferralEntryView, ReferralSummaryView } from '@common/api/featureService';
import dayjs from 'dayjs';

/** Preserve existing local-time parsing, including date-only inputs, while localizing display. */
export function formatReferralDate(value: string | null | undefined, locale: string): string | null {
    if (!value?.trim()) return null;
    const date = dayjs(value);
    if (!date.isValid()) return null;
    try {
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric', month: 'short', day: 'numeric',
        }).format(date.toDate());
    } catch {
        return null;
    }
}

/** Reserved credit remains unused until it is actually deducted from a bill. */
export function getRemainingReferralCreditAmount(summary: ReferralSummaryView | null | undefined): number | null {
    // Older servers omit the complete balance. Never replace a present but invalid
    // complete balance with legacy fields that can exclude checkout/recovery holds.
    if (summary && 'remainingCreditAmountMinor' in summary) {
        return isCreditAmount(summary.remainingCreditAmountMinor) ? summary.remainingCreditAmountMinor : null;
    }
    const available = summary?.availableCreditAmountMinor;
    const allocated = summary?.allocatedCreditAmountMinor;
    if (typeof available !== 'number' || !Number.isSafeInteger(available) || available < 0
        || typeof allocated !== 'number' || !Number.isSafeInteger(allocated) || allocated < 0) return null;
    const remaining = available + allocated;
    return Number.isSafeInteger(remaining) ? remaining : null;
}

/** Historical net earned credit is supplied by the server; do not derive it from returned history rows. */
export function getTotalEarnedReferralCreditAmount(summary: ReferralSummaryView | null | undefined): number | null {
    const totalEarned = summary?.totalEarnedCreditAmountMinor;
    return typeof totalEarned === 'number' && Number.isSafeInteger(totalEarned) && totalEarned >= 0
        ? totalEarned
        : null;
}

const isCreditAmount = (amount: unknown): amount is number => (
    typeof amount === 'number' && Number.isSafeInteger(amount) && amount >= 0
);

/** Complete balances distinguish consumption from reservations. Reversed rewards
 * may leave recovery debt not exposed by this API, so the difference is only safe
 * after a complete, unadjusted history corroborates the server's earned total.
 * Rows validate the summary; they never replace a missing aggregate. */
export function getTotalUsedReferralCreditAmount(
    summary: ReferralSummaryView | null | undefined,
    referrals: ReferralEntryView[] | undefined,
): number | null {
    const earned = getTotalEarnedReferralCreditAmount(summary);
    const remaining = summary?.remainingCreditAmountMinor;
    if (earned === null || !isCreditAmount(remaining) || remaining > earned) return null;
    if (!referrals || !isCreditAmount(summary?.totalReferrals) || referrals.length !== summary.totalReferrals) return null;
    let originalEarned = 0;
    for (const referral of referrals) {
        const credit = referral.credit;
        if (!credit) {
            if (referral.status !== 'ATTRIBUTED') return null;
            continue;
        }
        if (!isCreditAmount(credit.originalAmountMinor)
            || !credit.currency || credit.currency.toUpperCase() !== summary.currency?.toUpperCase()) return null;
        originalEarned += credit.originalAmountMinor;
        if (!Number.isSafeInteger(originalEarned)) return null;
    }
    // A lower net earned total means at least one reward was reversed. Without
    // consumed/recovered-debt aggregates we cannot report lifetime usage safely.
    if (originalEarned !== earned) return null;
    return earned - remaining;
}

export type ReferralPaymentProgress =
    | 'pending'
    | 'subscribed'
    | 'applying'
    | 'available'
    | 'allocated'
    | 'settled'
    | 'voided'
    | 'manualRecovery'
    | 'unknown';

/** The backend only creates a referral credit after the invitee's qualifying payment succeeds. */
export function getReferralPaymentProgress(referral: ReferralEntryView): ReferralPaymentProgress {
    if (!referral.credit && isPaidInviteeSubscription(referral.referredSubscriptionStatus)) return 'subscribed';
    if (!referral.credit) return referral.status === 'ATTRIBUTED' ? 'pending' : 'unknown';
    switch (referral.credit.status) {
        case 'PENDING': return 'applying';
        case 'AVAILABLE': return 'available';
        case 'ALLOCATED': return 'allocated';
        case 'SETTLED': return 'settled';
        case 'VOIDED': return 'voided';
        case 'MANUAL_RECOVERY_REQUIRED': return 'manualRecovery';
        default: return 'unknown';
    }
}

/** A positive credit is created only once the qualifying first payment has completed. */
export function getPaidWorkspaceCount(referrals: ReferralEntryView[]): number | null {
    if (!referrals.every((entry) => entry.credit || entry.status === 'ATTRIBUTED' || isPaidInviteeSubscription(entry.referredSubscriptionStatus))) return null;
    return referrals.filter((entry) => {
        const amount = entry.credit?.originalAmountMinor;
        return (typeof amount === 'number' && Number.isSafeInteger(amount) && amount > 0)
            || isPaidInviteeSubscription(entry.referredSubscriptionStatus);
    }).length;
}

function isPaidInviteeSubscription(status: string | null | undefined): boolean {
    return status === 'active' || status === 'pending_cancellation';
}

/** Amounts are server-provided minor units; a missing value is not zero. */
export function formatReferralCreditAmount(
    amountMinor: number | null | undefined,
    currency: string | null | undefined,
    locale: string,
): string | null {
    if (typeof amountMinor !== 'number' || !Number.isSafeInteger(amountMinor) || amountMinor < 0
        || !currency || !/^[A-Za-z]{3}$/.test(currency)) return null;
    try {
        const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
        const precision = formatter.resolvedOptions().maximumFractionDigits;
        return formatter.format(amountMinor / (10 ** (precision ?? 2)));
    } catch {
        return null;
    }
}
